import {
  chooseHardwareSendCodec,
  type EncodeProfileProbe,
  type SendCodecDecision,
} from './hardwareCodecPolicy';
import { hardwareVideoCodecFamily } from './mediaPolicy/VideoCodecPolicy';
import { VideoSenderSuspension } from './VideoSenderSuspension';

type CodecCapability = RTCRtpCodec;
type SenderParameters = RTCRtpSendParameters & { codecs?: CodecCapability[] };

export type SenderCodecPinOptions = {
  guestHardwareDecodeFamilies?: string[];
  degradationPreference?: 'maintain-framerate' | 'maintain-resolution' | 'balanced';
};

/** One serialized transaction: hardware HEVC, hardware H.264, then terminal failure. */
export class SenderCodecPin {
  public async pin(
    sender: RTCRtpSender,
    options: SenderCodecPinOptions,
    probe: EncodeProfileProbe | null,
  ): Promise<SendCodecDecision<CodecCapability>> {
    const parameters = sender.getParameters() as SenderParameters;
    const negotiated = parameters.codecs ?? [];
    if (!parameters.encodings?.length || !negotiated.length) {
      // The normal state immediately after connect: no encodings are attached yet. Return a
      // retry-pending decision rather than a bare null, so the caller cannot mistake the
      // absence of an answer for acceptance and proceed on the browser default codec.
      return {
        codec: null, considered: [], hardware: [], guestSoftwareDecode: [],
        pinAccepted: false, pinRetryPending: true,
      };
    }

    let remaining = negotiated;
    let decision = await chooseHardwareSendCodec({
      negotiatedCodecs: remaining,
      probe,
      guestHardwareDecodeFamilies: options.guestHardwareDecodeFamilies,
    });
    if (!decision.codec) return { ...decision, pinAccepted: false };
    if (await this.apply(sender, decision.codec, options.degradationPreference)) {
      return { ...decision, pinAccepted: true };
    }

    if (decision.family === 'hevc') {
      remaining = negotiated.filter(codec => hardwareVideoCodecFamily(codec.mimeType) !== 'hevc');
      decision = await chooseHardwareSendCodec({
        negotiatedCodecs: remaining,
        probe,
        guestHardwareDecodeFamilies: options.guestHardwareDecodeFamilies,
      });
      if (decision.codec && await this.apply(sender, decision.codec, options.degradationPreference)) {
        return { ...decision, pinAccepted: true };
      }
    }
    return { ...decision, codec: null, pinAccepted: false, failureReason: 'sender-pin-rejected' };
  }

  private async apply(
    sender: RTCRtpSender,
    codec: CodecCapability,
    degradationPreference?: SenderCodecPinOptions['degradationPreference'],
  ): Promise<boolean> {
    const fresh = sender.getParameters() as SenderParameters;
    if (!fresh.encodings?.length) return false;
    fresh.encodings[0].scaleResolutionDownBy = 1;
    fresh.degradationPreference = degradationPreference ?? 'maintain-resolution';
    (fresh.encodings[0] as { codec?: CodecCapability }).codec = codec;
    fresh.encodings[0].active = !VideoSenderSuspension.holds(sender);
    try {
      await sender.setParameters(fresh);
      return true;
    } catch {
      return false;
    }
  }
}
